// What a spectator receives, in the order a local sim can use it.
//
// The match-server sends a header plus every frame so far (`spectate:ack`) and
// then live frames. The sim cannot exist before the header arrives, so frames
// wait here until a consumer attaches; once attached they drain in tick order
// and later frames go straight through. Applying is FrameSync's job (it drops
// stale frames and reports gaps), so this class only buffers and orders.

import type { ProvisionedMap, Ruleset, Seat } from '@aod/protocol';
import type { MatchInfo } from '../multiplayer/types.js';

export interface SpectateHeader {
  readonly matchId: string;
  readonly seed: number;
  readonly format: string;
  readonly tickRateHz: number;
  readonly seatCount: number;
  readonly ruleset?: Ruleset;
  readonly seats?: readonly Seat[];
  /** Team of each seat, when the match had teams. */
  readonly teams?: readonly number[];
  readonly map?: unknown;
}

export interface SpectateFrame {
  readonly tick: number;
  readonly frame: Record<string, readonly unknown[]>;
  readonly hash?: string;
}

export class SpectateFeed {
  private history: SpectateFrame[] = [];
  private sink: ((f: SpectateFrame) => void) | null = null;

  /** Frames received so far. */
  get length(): number {
    return this.history.length;
  }

  push(frames: readonly SpectateFrame[]): void {
    const sorted = [...frames].sort((a, b) => a.tick - b.tick);
    for (const f of sorted) {
      // A frame already held (an ack racing a live frame) is not stored twice.
      const last = this.history[this.history.length - 1];
      if (last && f.tick <= last.tick) continue;
      this.history.push(f);
      this.sink?.(f);
    }
  }

  /**
   * Hand over every frame held, then every later one. The history is kept, so
   * a second consumer (the viewer switching seats rebuilds its sim) replays the
   * match from the start. Returns a detach.
   */
  attach(sink: (f: SpectateFrame) => void): () => void {
    this.sink = sink;
    for (const f of [...this.history]) sink(f);
    return () => {
      if (this.sink === sink) this.sink = null;
    };
  }
}

/**
 * The match the header describes, shaped like the one a player gets in
 * `match:found`, so the same sim hook builds the same board. A spectator is
 * seat-less: nobody here is "local".
 */
export function headerToMatchInfo(h: SpectateHeader, serverUrl: string): MatchInfo {
  const players = Array.from({ length: h.seatCount }, (_, i) => ({
    accountId: `seat-${i}`,
    displayName: `Player ${i + 1}`,
  }));
  const map = h.map
    ? ({
        document: h.map,
        ...(h.ruleset ? { ruleset: h.ruleset } : {}),
        ...(h.seats ? { seats: h.seats } : {}),
      } as unknown as ProvisionedMap)
    : undefined;
  return {
    matchId: h.matchId,
    seed: h.seed.toString(16),
    players,
    aiSlots: [],
    localPlayerId: '',
    serverUrl,
    totalPlayers: h.seatCount,
    playerAlignments: {},
    ...(map ? { map } : {}),
    ...(!map && h.ruleset ? { ruleset: h.ruleset } : {}),
    ...(!map && h.seats ? { seats: h.seats } : {}),
    ...(h.teams ? { teams: h.teams } : {}),
  };
}
